import { type TutorialLevel } from "./tutorial-data";

const PAOLO_TUT_LVL_4: TutorialLevel = {
  name: "Advanced Logic",
  description:
    "Learn advanced techniques that will help you tackle larger puzzles.",
  gridLayout: [
    [0, 1, 1, 0, 1, 1, 1],
    [1, 0, 0, 1, 1, 0, 1],
    [1, 1, 1, 0, 1, 1, 0],
    [0, 0, 0, 0, 0, 0, 0],
    [1, 0, 0, 0, 0, 0, 1],
    [1, 1, 1, 1, 1, 1, 0],
    [0, 0, 0, 0, 0, 0, 0],
  ],
  phases: [
    {
      waitFor: "message",
      allowGridInput: true,
      message: <p>Now let's look at a larger puzzle.</p>,
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          There are a few 0's, so just like before,{" "}
          <strong>X-out all the squares in these rows</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "row",
          index: 3,
        },
        {
          type: "row",
          index: 6,
        },
      ],
      requireX: [
        [0, 3],
        [1, 3],
        [2, 3],
        [3, 3],
        [4, 3],
        [5, 3],
        [6, 3],
        [0, 6],
        [1, 6],
        [2, 6],
        [3, 6],
        [4, 6],
        [5, 6],
        [6, 6],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Now look at the <strong>first column</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          There must be two separate blocks of two colored-in squares. There are{" "}
          <strong>only two ways</strong> for this to happen.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      demoFadeTiles: [
        [
          [0, 0],
          [0, 1],
          [0, 4],
          [0, 5],
        ],
        [
          [0, 1],
          [0, 2],
          [0, 4],
          [0, 5],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          Color in the <strong>three squares</strong> that are guaranteed to be
          colored-in to continue.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 0,
        },
      ],
      requireColor: [
        [0, 1],
        [0, 4],
        [0, 5],
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          Great job! Now, look at <strong>the row with the 6</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "row",
          index: 5,
        },
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          We have one colored-in square at the edge, which{" "}
          <strong>forces the placement of the other five</strong>. Color-in
          these squares to continue.
        </p>
      ),
      requireColor: [
        [1, 5],
        [2, 5],
        [3, 5],
        [4, 5],
        [5, 5],
      ],
      vignettes: [
        {
          type: "row",
          index: 5,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <>
          <p>
            Now let's turn to the <strong>rightmost column.</strong>
          </p>
          <p>Think about where you can place the one colored-in square.</p>
        </>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: (
        <p>
          If you placed a single square here, it'd be{" "}
          <strong>impossible</strong> to place a block of two squares above it
          with white space in between.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      highlightRects: [
        {
          start: [6, 2],
          end: [6, 2],
        },
      ],
      demoFadeTiles: [
        [[6, 2]],
        [
          [6, 0],
          [6, 1],
          [6, 2],
        ],
      ],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          Therefore, the colored-in square must go here.{" "}
          <strong>Fill it in to continue</strong>.
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      highlightRects: [{ start: [6, 4], end: [6, 4] }],
      requireColor: [[6, 4]],
    },
    {
      waitFor: "tileFillExact",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <p>
          <strong>The 2 clue</strong> in this column also allows us to know the
          location of <strong>one more</strong> colored-in square, using the
          rules we've learned.{" "}
          <strong>Color in that square to continue.</strong>
        </p>
      ),
      vignettes: [
        {
          type: "column",
          index: 6,
        },
      ],
      requireColor: [[6, 1]],
    },
    {
      waitFor: "tileFill",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <>
          <p>
            In this row, there is <strong>one square</strong> that is guaranteed
            to be colored-in. <strong>Color it in to continue</strong>.
          </p>
          <p>
            <i>
              Placing X's in squares that cannot be shaded in will make this
              easier to visualize.
            </i>
          </p>
        </>
      ),
      vignettes: [
        {
          type: "row",
          index: 1,
        },
      ],
      requireColor: [[3, 1]],
    },
    {
      waitFor: "puzzleComplete",
      allowGridInput: true,
      allowToolSwitch: true,
      message: (
        <>
          <p>Now you have everything you need to finish the puzzle.</p>
          <p>
            <strong>Do that now</strong>.
          </p>
        </>
      ),
    },
    {
      waitFor: "message",
      allowGridInput: false,
      message: <p>Great job!</p>,
    },
  ],
};

export default PAOLO_TUT_LVL_4;
